import { cloneElement, useCallback, useEffect, useRef, type ReactElement, type ReactNode } from "react";
import { cx } from "./cx";
import { Chevron } from "./Popover";
import { useControllableOpen, useDismiss, useFloating, type OpenControl } from "./hooks";

export type MenuItem =
  | "sep"
  | { heading: ReactNode }
  | { key: string; label: ReactNode; onSelect?: () => void; danger?: boolean; disabled?: boolean; hint?: ReactNode };

export interface DropdownProps extends OpenControl {
  items: MenuItem[];
  label?: string;
  trigger?: ReactElement;
  variant?: "primary" | "secondary" | "ghost";
  size?: "sm";
  iconOnly?: boolean;
  align?: "start" | "end";
  className?: string;
}

/**
 * 按钮触发的操作菜单。打开后焦点落在菜单上，方向键进入菜单项，Enter/空格执行。
 * 焦点在菜单里时按 Esc 只关菜单并把焦点还给触发按钮，不冒泡，不会连带关掉外层的抽屉或对话框（它们在 document 上监听，同 Tooltip）。
 */
export function Dropdown({ items, label, trigger, variant = "secondary", size, iconOnly, align, className, ...ctl }: DropdownProps) {
  const [open, setOpen] = useControllableOpen(ctl);
  const ref = useRef<HTMLSpanElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const close = useCallback(() => setOpen(false), [setOpen]);
  useDismiss(ref, open, close);
  const floating = useFloating(ref, open, align, close);
  useEffect(() => { if (open) menuRef.current?.focus({ preventScroll: true }); }, [open]);

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      e.stopPropagation(); // React 的监听先于 document 上的原生监听
      close();
      ref.current?.querySelector<HTMLElement>("[aria-haspopup=menu]")?.focus();
      return;
    }
    const items = Array.from(menuRef.current?.querySelectorAll<HTMLButtonElement>("[role=menuitem]:not(:disabled)") ?? []);
    if (!items.length) return;
    const i = items.indexOf(document.activeElement as HTMLButtonElement);
    if (e.key === "ArrowDown") { e.preventDefault(); (items[i + 1] ?? items[0]).focus(); }
    else if (e.key === "ArrowUp") { e.preventDefault(); (i < 0 ? items[items.length - 1] : items[i - 1] ?? items[items.length - 1]).focus(); }
    else if (e.key === "Home") { e.preventDefault(); items[0].focus(); }
    else if (e.key === "End") { e.preventDefault(); items[items.length - 1].focus(); }
    else if (e.key === "Tab") close();
  };

  const t = trigger as ReactElement<Record<string, unknown>> | undefined;
  const triggerEl = t ? (
    cloneElement(t, { onClick: () => setOpen(!open), "aria-expanded": open, "aria-haspopup": "menu" })
  ) : (
    <button type="button" className={cx("ax-btn", `ax-btn-${variant}`, size === "sm" && "ax-btn-sm", iconOnly && "ax-btn-icon")} onClick={() => setOpen(!open)} aria-expanded={open} aria-haspopup="menu" aria-label={iconOnly ? label : undefined}>
      {iconOnly ? "⋯" : <>{label}<Chevron /></>}
    </button>
  );

  return (
    <span className={cx("ax-popover", className)} ref={ref}>
      {triggerEl}
      {open && (
        <div className={cx("ax-popover-panel", align === "end" && "end")} style={floating} role="menu" tabIndex={-1} ref={menuRef} onKeyDown={onKey}>
          <div className="ax-menu">
            {items.map((it, idx) => {
              if (it === "sep") return <div key={`s${idx}`} className="ax-menu-sep" role="separator" />;
              if ("heading" in it) return <div key={`h${idx}`} className="ax-menu-label">{it.heading}</div>;
              return (
                <button key={it.key} type="button" role="menuitem" className={cx("ax-menu-item", it.danger && "danger")} disabled={it.disabled} onClick={() => { close(); it.onSelect?.(); }}>
                  {it.label}
                  {it.hint != null && <span className="hint">{it.hint}</span>}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </span>
  );
}
